/**
 * Auth Context
 *
 * Manages authentication state, session timeout, and user sync
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCurrentUser,
  onAuthStateChange,
  signOut as authSignOut,
  syncUserToDatabase
} from '../services/authService';
import {
  startTokenRefreshService,
  stopTokenRefreshService
} from '../services/gmailTokenRefreshService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Use a REF for lastActivity instead of state.
  // Using state caused a re-render on every touchstart (via updateActivity),
  // which replaced DOM elements before iOS Safari could synthesize click events,
  // killing all taps on mobile after the first touch.
  const lastActivityRef = useRef(Date.now());
  const navigate = useNavigate();

  // 30 minute timeout (in milliseconds)
  const IDLE_TIMEOUT = 30 * 60 * 1000;

  // Update last activity time on user interaction — uses ref, NO re-render
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Check for idle timeout
  useEffect(() => {
    if (!user) return;

    const checkIdleTimeout = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;

      if (timeSinceLastActivity >= IDLE_TIMEOUT) {
        handleSignOut();
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkIdleTimeout);
  }, [user]);

  // Set up activity listeners
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user, updateActivity]);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Quick synchronous check for cached session first
        // This prevents showing loading screen if session is already loaded
        const currentUser = await getCurrentUser();

        if (currentUser) {
          // --- QA ENVIRONMENT ACCESS CONTROL (cached session) ---
          // Quick synchronous check: only run QA gate on QA domains
          const isQADomain = window.location.hostname.includes('qa.subby.io') ||
            (process.env.REACT_APP_SUPABASE_URL || '').includes('arlpsjontzyavqcmldsk');
          if (isQADomain) {
            try {
              const { isUserAuthorizedForQA } = await import('../services/qaService');
              const isAuthorized = await isUserAuthorizedForQA(currentUser.email);
              if (!isAuthorized) {
                console.warn(`QA access denied for cached session: ${currentUser.email}`);
                localStorage.setItem('qa_access_denied', 'true');
                await authSignOut();
                setUser(null);
                setLoading(false);
                window.location.href = '/login';
                return;
              }
            } catch (qaError) {
              console.error('Error checking QA access on init:', qaError);
            }
          }
          // --- END QA ACCESS CONTROL ---

          // Set user immediately for instant UI
          setUser(currentUser);
          setLoading(false);

          // Sync user to database in background (non-blocking)
          syncUserToDatabase(currentUser).catch(err => {
            console.error('Background user sync error:', err);
          });

          // Start Gmail token refresh service in background (non-blocking)
          // Use setTimeout to defer it completely, allowing UI to render first
          setTimeout(() => {
            startTokenRefreshService().catch(err => {
              console.error('Error starting token refresh service:', err);
            });
          }, 100); // 100ms delay to let UI render first
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
      }
    };

    initAuth();

    // Cleanup: Stop token refresh when component unmounts
    return () => {
      stopTokenRefreshService();
    };
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const subscription = onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // Clean auth code from URL (PKCE one-time code is no longer needed)
        const url = new URL(window.location.href);
        if (url.searchParams.has('code') && url.pathname !== '/gmail-callback') {
          url.searchParams.delete('code');
          window.history.replaceState({}, '', url.pathname);
        }
        // --- QA ENVIRONMENT ACCESS CONTROL ---
        // Quick synchronous check: only run QA gate on QA domains
        const isQADomain = window.location.hostname.includes('qa.subby.io') ||
          (process.env.REACT_APP_SUPABASE_URL || '').includes('arlpsjontzyavqcmldsk');
        if (isQADomain) {
          try {
            const { isUserAuthorizedForQA } = await import('../services/qaService');
            const isAuthorized = await isUserAuthorizedForQA(session.user.email);

            if (!isAuthorized) {
              console.warn(`Access denied for QA environment: ${session.user.email}`);
              localStorage.setItem('qa_access_denied', 'true');
              await authSignOut();
              setUser(null);
              window.location.href = '/login';
              return;
            }
          } catch (qaError) {
            console.error('Error checking QA access control:', qaError);
          }
        }
        // --- END QA ENVIRONMENT ACCESS CONTROL ---

        // Sync user to database on sign in
        await syncUserToDatabase(session.user);
        setUser(session.user);
        lastActivityRef.current = Date.now();

        // Try to auto-add Gmail account from OAuth session BEFORE navigation
        // (Must happen before navigating to /onboarding so the account exists when Onboarding checks for it)
        try {
          const { addGmailAccountFromSession } = await import('../services/gmailOAuthService');
          const gmailResult = await addGmailAccountFromSession(session);

          // Check if user didn't grant Gmail permission
          if (gmailResult?.scopeMissing) {
            console.warn('⚠️ Gmail scope was not granted during signup');
            try {
              const { createNotification } = await import('../services/notificationService');
              await createNotification({
                userId: session.user.id,
                type: 'warning',
                title: 'Gmail Access Not Granted',
                message: 'You unchecked the Gmail permission during signup. To sync your subscriptions from Gmail, go to Settings and reconnect your Gmail account with the checkbox enabled.',
                data: { action: 'reconnect_gmail' }
              });
            } catch (notifError) {
              console.error('Error creating Gmail scope warning notification:', notifError);
            }
          }
        } catch (gmailError) {
          console.error('Error auto-adding Gmail account:', gmailError);
          // Don't block login if Gmail account creation fails
        }

        // Navigate to the correct page now that Gmail account is saved
        // Only navigate if user is on /login or /signup (i.e., returning from OAuth callback)
        // Don't navigate if already on a protected page (session restore fires SIGNED_IN too)
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/signup') {
          try {
            const { hasCompletedOnboarding } = await import('../services/onboardingService');
            const completed = await hasCompletedOnboarding(session.user.id);
            if (completed) {
              navigate('/dashboard');
            } else {
              navigate('/onboarding');
            }
          } catch (navError) {
            console.error('Error checking onboarding status:', navError);
            navigate('/dashboard'); // Fallback to dashboard
          }
        }

        // Apply selected plan from signup if exists
        const selectedPlan = localStorage.getItem('selected_plan');
        const needsPayment = localStorage.getItem('needs_payment');

        if (selectedPlan) {
          try {
            // Check if user selected Plus plan and needs to pay
            if (needsPayment === 'true') {
              console.log('🔄 Plus plan selected - redirecting to payment...');
              // Clear the flags
              localStorage.removeItem('selected_plan');
              localStorage.removeItem('needs_payment');

              // Redirect to Stripe Checkout
              const { createCheckoutSession, STRIPE_PRICE_IDS } = await import('../services/stripeService');
              await createCheckoutSession(STRIPE_PRICE_IDS.PLUS_MONTHLY);
              return; // Exit early as we're redirecting to Stripe
            } else {
              // Basic plan or other - apply directly
              const { updateUserPlan } = await import('../services/planService');
              await updateUserPlan(selectedPlan);
              console.log(`✅ Applied ${selectedPlan} plan for new user`);
              localStorage.removeItem('selected_plan');
            }
          } catch (error) {
            console.error('Error applying selected plan:', error);
          }
        }

        // Start Gmail token refresh service for newly signed in user
        startTokenRefreshService().catch(err => {
          console.error('Error starting token refresh service:', err);
        });

        // Trigger Gmail sync for Plus users on login (once per session)
        // Use sessionStorage to prevent sync on page refresh - only on actual login
        const triggerLoginSync = async () => {
          try {
            // Check if we've already synced in this session
            const syncedThisSession = sessionStorage.getItem('gmail_sync_done');
            if (syncedThisSession) {
              console.log('ℹ️ Gmail sync already done this session - skipping');
              return;
            }

            // Set flag IMMEDIATELY to prevent race condition
            // (Supabase fires SIGNED_IN twice rapidly)
            sessionStorage.setItem('gmail_sync_done', 'true');

            const { getUserFeatures, getUserPlan } = await import('../services/planService');
            const plan = await getUserPlan();
            const features = await getUserFeatures();

            console.log('🔍 Login sync check:', {
              plan,
              canUseAdvancedAI: features?.canUseAdvancedAIClassification,
              allFeatures: features
            });

            // Only sync for Plus users
            if (features?.canUseAdvancedAIClassification) {
              console.log('✅ Plus user detected - triggering login Gmail sync');

              const { getGmailAccounts, syncGmailAccount } = await import('../services/gmailService');

              // Use setTimeout to avoid blocking the login flow
              setTimeout(async () => {
                try {
                  const accounts = await getGmailAccounts();
                  if (!accounts || accounts.length === 0) {
                    console.log('ℹ️ No Gmail accounts to sync on login');
                    return;
                  }

                  console.log(`📧 Login sync: syncing ${accounts.length} Gmail account(s)`);

                  // Sync each account sequentially so the banner shows each one
                  try {
                    for (const account of accounts) {
                      try {
                        // Set syncing indicator in localStorage (picked up by Settings/Subscriptions page)
                        localStorage.setItem('syncingAccountId', account.id);
                        localStorage.setItem('syncingStartedAt', Date.now().toString());

                        console.log(`🔄 Login sync: syncing ${account.gmail_email}...`);
                        await syncGmailAccount(account.id);
                        console.log(`✅ Login sync: ${account.gmail_email} complete`);

                        // Notify Subscriptions page to refresh after this account's sync
                        window.dispatchEvent(new CustomEvent('gmail-sync-complete', {
                          detail: { gmailAccountId: account.id, email: account.gmail_email }
                        }));
                      } catch (err) {
                        console.error(`Error syncing ${account.gmail_email} on login:`, err);
                      }
                    }
                  } finally {
                    // ALWAYS clear the syncing indicator, even if sync errors or hangs
                    localStorage.removeItem('syncingAccountId');
                    localStorage.removeItem('syncingStartedAt');
                  }

                  console.log('✅ Login sync complete for all accounts');
                } catch (err) {
                  console.error('Error syncing Gmail accounts on login:', err);
                  localStorage.removeItem('syncingAccountId');
                  localStorage.removeItem('syncingStartedAt');
                }
              }, 2000); // Wait 2 seconds after login
            } else {
              console.log('ℹ️ Basic user - skipping login Gmail sync');
            }
          } catch (error) {
            console.error('Error checking plan for login sync:', error);
          }
        };

        triggerLoginSync();
      } else if (event === 'SIGNED_OUT') {
        // Only redirect to login if user was previously signed in
        // (not on initial page load where Supabase fires SIGNED_OUT with no session)
        setUser(prev => {
          if (prev) {
            navigate('/login');
          }
          return null;
        });
        // Stop token refresh service on sign out
        stopTokenRefreshService();
        // Clear the session sync flag so next login will sync
        sessionStorage.removeItem('gmail_sync_done');
      } else if (event === 'TOKEN_REFRESHED') {
        setUser(session?.user || null);
        lastActivityRef.current = Date.now();
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [navigate]);

  // Sign out handler
  const handleSignOut = useCallback(async () => {
    try {
      await authSignOut();
      setUser(null);
      // Clear the session sync flag so next login will sync
      sessionStorage.removeItem('gmail_sync_done');
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [navigate]);

  const value = useMemo(() => ({
    user,
    loading,
    signOut: handleSignOut,
    isAuthenticated: !!user,
    updateActivity
  }), [user, loading, handleSignOut, updateActivity]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
